import React, { useCallback, useState, useEffect, useRef } from 'react';
import { connect } from 'redux-bundler-react';

import { isDateValid, isValidText, sanitizeText } from '@src/utils/helpers';
import genericSecondaryModal from '@forms/components/modals/genericSecondaryModal';
import { decimalNumberRegex } from '@src/utils/regex';
import { ErrorMessages } from '@src/utils/enums';
import statusModal from '@forms/components/modals/statusModal';
import { useLookupTablesQuery } from '@api/services/lookup/hooks';
import { useModal } from '@stores';
import Select from 'react-select';
import { AorQueries } from '@api/services/aor';

const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const formatSelectValue = (value, options) => {
  if (!value || !options) return '';
  return options?.find((option) => option.value === value) || { value: value, label: value };
};

const getSelectOptionValue = (option) => {
  if (!option) return null;
  return option.value;
};

const hasValueChanged = (prevValue, newValue) => {
  if (prevValue === null && newValue === null) return false;
  if (prevValue === undefined && newValue === undefined) return false;
  return prevValue !== newValue;
};

export const TableCell = connect(
  'selectRequestFormData',
  ({ requestFormData, getValue, row, column, table, cell, cellError }) => {
    const columnMeta = column.columnDef.meta;
    const type = columnMeta?.type ?? 'text';
    const tableMeta = table.options.meta;
    const initialValue = getValue();
    const [value, setValue] = useState(
      type === 'combobox' ? formatSelectValue(initialValue, columnMeta?.options) : initialValue
    );
    const previousValueRef = useRef(
      type === 'combobox' ? formatSelectValue(initialValue, columnMeta?.options) : initialValue
    );

    const { openSecondaryModal } = useModal();
    const { data: lookupTableData } = useLookupTablesQuery();

    // Setting the End Date
    var endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 100);
    endDate = new Date(endDate).toISOString().slice(0, 10);

    const debouncedUpdateRef = useRef();

    const determineRegulation = (state) => {
      const regulationIndex = lookupTableData?.StateRegulations?.findIndex(
        (statereg) => statereg?.state?.toUpperCase() === state?.toUpperCase()
      );
      const regulation = lookupTableData?.StateRegulations?.[regulationIndex]?.regulationType;
      return regulation;
    };

    useEffect(() => {
      debouncedUpdateRef.current = debounce((newValue) => {
        if (tableMeta?.updateData) {
          tableMeta?.updateData(row.index, column.id, type === 'number' ? Number(newValue) : (newValue ?? newValue));
        }
      }, 500);
    }, [row.index, column.id, tableMeta?.updateData, type, tableMeta]);

    const updateValue = useCallback((newValue) => {
      debouncedUpdateRef.current(newValue);
    }, []);

    const handleBlur = async (e) => {
      const blurValue = e?.target?.value;
      const valueBeforeBlur = previousValueRef.current?.toString();

      // Check to see if field value has changed
      if (hasValueChanged(valueBeforeBlur, blurValue)) {
        // Clear field if value is 0 or is a negative number
        if (
          String(blurValue) === '0' ||
          (String(blurValue)[0] === '-' && column.id !== 'longitude' && column.id !== 'latitude')
        ) {
          setValue('');
          updateValue('');
        }

        // If latitude or longitude is cleared, revert to project location coordinates
        if (
          (column.id === 'latitude' || column.id === 'longitude') &&
          (blurValue === '' || blurValue == null || blurValue === '0')
        ) {
          // Try to get project location coordinates from requestFormData
          const projectLocation = requestFormData?.location;
          const projectLocationLatitude = projectLocation?.latitude;
          const projectLocationLongitude = projectLocation?.longitude;
          if (projectLocation) {
            const coordValue =
              column.id === 'latitude'
                ? columnMeta.formatCoordStr(projectLocationLatitude)
                : columnMeta.formatCoordStr(projectLocationLongitude);
            if (coordValue !== undefined && coordValue !== null && coordValue !== '') {
              setValue(coordValue);
              updateValue(coordValue);
              return;
            }
          }
        }
        if (columnMeta?.formatCoordStr) {
          if (!isNaN(blurValue)) {
            const formattedCoord = columnMeta.formatCoordStr(blurValue);
            setValue(formattedCoord);
            updateValue(formattedCoord);
          }
        }
        if (columnMeta?.validateDistrict === true) {
          const latitude = row.getValue('latitude');
          const longitude = row.getValue('longitude');
          const projectLocation = requestFormData?.location;
          const projectLocationLatOrLong =
            column.id === 'latitude'
              ? columnMeta.formatCoordStr(projectLocation?.latitude)
              : columnMeta.formatCoordStr(projectLocation?.longitude ?? '');
          if (latitude && longitude) {
            const geometry = { type: 'Point', coordinates: [longitude, latitude] };
            const isValid = await AorQueries.isGeometryInValidDistrict(geometry);

            if (isValid === false) {
              openSecondaryModal(statusModal, {
                msg: ErrorMessages.InvalidDistrict,
                status: 'Invalid Resource Location',
              });
              setValue(projectLocationLatOrLong);
              updateValue(projectLocationLatOrLong);
            }
          }
        }

        if (type === 'date') {
          if (isDateValid(value, columnMeta?.min, endDate) === false) {
            // @TODO: Workaround to rerender the cell - generates console warning for invalid formatting
            setValue('0000-0-0');
            updateValue('');
          }
        }
        previousValueRef.current = blurValue;
      }
    };

    const handleChange = async (e) => {
      const inputValue = e?.target?.value ?? '';
      const prevInputValue = value?.toString() ?? '';

      if (hasValueChanged(prevInputValue, inputValue)) {
        if (inputValue === '') {
          setValue(null);
          updateValue(null);
        } else if (columnMeta?.isNumber === true) {
          if (decimalNumberRegex.test(inputValue)) {
            setValue(inputValue);
            updateValue(inputValue);
          }
        } else {
          setValue(inputValue);
          updateValue(inputValue);
        }
        if (columnMeta?.dependency === 'regulationType') {
          const applicableRegulation = determineRegulation(e?.target?.value);
          tableMeta?.updateData(row.index, 'regulationType', applicableRegulation);
        }
      }
    };

    const handleComboboxChange = async (option) => {
      const optionValue = getSelectOptionValue(option);
      const optionValueBeforeBlur = value?.value;

      if (hasValueChanged(optionValueBeforeBlur, optionValue)) {
        setValue(option);
        updateValue(optionValue);

        if (columnMeta?.dependency === 'regulationType') {
          const applicableRegulation = determineRegulation(optionValue);
          tableMeta?.updateData(row.index, 'regulationType', applicableRegulation);
        }
      }
    };

    const handleComboboxBlur = async (option) => {
      const optionValue = getSelectOptionValue(option);
      const optionValueBeforeBlur = previousValueRef.current;

      if (hasValueChanged(optionValueBeforeBlur, optionValue)) {
        previousValueRef.current = optionValue;
      }
    };

    // Handling special characters
    useEffect(() => {
      if (value && typeof value === 'string' && type === 'text') {
        if (isValidText(value)) {
          setValue(value);
          updateValue(value);
        } else {
          // Optionally handle invalid input here, such as warning the user
          const msg = ErrorMessages.InvalidCharacters;
          openSecondaryModal(genericSecondaryModal, { title: 'Invalid Characters', msg: msg });
          setValue(sanitizeText(value));
          updateValue(sanitizeText(value));
        }
      }
    }, [value, type, setValue, updateValue, openSecondaryModal]);

    const getMaxLength = () => {
      if (type === 'text') {
        return columnMeta?.maxLength || 256;
      }
      return null;
    };

    return type === 'combobox' ? (
      <Select
        value={value}
        id={cell.id}
        className={`width-full ${cellError ? 'cell-error' : ''}`}
        onChange={handleComboboxChange}
        onBlur={handleComboboxBlur}
        options={columnMeta?.options}
        menuPortalTarget={document.body}
        menuPosition='fixed'
        menuPlacement='auto'
        placeholder='Select...'
        isDisabled={columnMeta?.readOnly}
        isRequired={columnMeta?.required}
        isClearable={!columnMeta?.required}
        isSearchable={false}
        aria-label={columnMeta?.label || 'Select an option'}
        components={{
          IndicatorSeparator: () => null,
        }}
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: 30,
            height: 30,
            width: '100%',
            fontFamily: 'inherit',
            borderColor: 'hsl(0, 0%, 80%)',
            ':hover': { borderColor: 'hsl(0, 0%, 80%)' },
            boxShadow: 'none',
            ...(state.isFocused && {
              outline: '0.25rem solid #2491ff',
              outlineOffset: '0rem',
            }),
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: 30,
            padding: '0 8px',
          }),
          input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            lineHeight: 1.15,
            height: 30,
            color: 'black',
          }),
          singleValue: (provided) => ({
            ...provided,
            lineHeight: '30px',
            height: 30,
            color: 'black',
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: 30,
            paddingLeft: 0,
            color: 'black',
          }),
          clearIndicator: (provided) => {
            return {
              ...provided,
              height: 30,
              paddingTop: 5,
              paddingLeft: 5,
              paddingRight: 0,
              margin: 0,
              color: cellError ? 'hsl(0, 0%, 25%)' : 'hsl(0, 0%, 80%)',
              ':hover': {
                color: 'red',
                cursor: 'pointer',
              },
            };
          },
          dropdownIndicator: (provided) => ({
            ...provided,
            height: 30,
            paddingTop: 5,
            paddingLeft: 0,
            paddingRight: 2,
            color: cellError ? 'hsl(0, 0%, 25%)' : 'hsl(0, 0%, 80%)',
            ':hover': {
              color: 'black',
              cursor: 'pointer',
            },
          }),
          menu: (provided) => ({
            ...provided,
            width: 'auto',
            fontFamily: 'inherit',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          placeholder: (provided) => ({
            ...provided,
            color: 'hsl(0, 0%, 25%)',
          }),
        }}
        menuShouldScrollIntoView={false}
      />
    ) : type === 'select' ? (
      <select
        aria-label={columnMeta?.label || 'Select an option'}
        disabled={columnMeta?.readOnly}
        id={cell.id}
        onBlur={handleBlur}
        onChange={handleChange}
        required={columnMeta?.required}
        style={{ width: '100%', borderColor: 'hsl(0, 0%, 80%)' }}
        value={value ?? ''}
      >
        <option key={0} value='' className='none' style={{ display: 'none' }}>
          -- Select a value --
        </option>
        {columnMeta?.options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        aria-label={columnMeta?.label || 'Enter value'}
        id={cell.id}
        max={type === 'date' ? endDate : undefined}
        maxLength={getMaxLength()}
        min={type === 'date' ? columnMeta?.min : undefined}
        onBlur={handleBlur}
        onChange={handleChange}
        readOnly={columnMeta?.readOnly}
        required={columnMeta?.required}
        style={{ width: '100%', borderColor: 'hsl(0, 0%, 80%)' }}
        type={type}
        value={value ?? ''}
      />
    );
  }
);
